import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Loader from '../../components/common/Loader';
import { useToast } from '../../context/ToastContext';
import { handleApiError } from '../../utils/helpers';
import axiosInstance from '../../api/axios';

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = { ACTIVE: 'bg-green-100 text-green-800', PAUSED: 'bg-yellow-100 text-yellow-800', DRAFT: 'bg-gray-100 text-gray-700', ENDED: 'bg-red-100 text-red-800' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>;
};

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
const ConfirmDialog = ({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', confirmClass = 'bg-red-600 hover:bg-red-700', loading }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <button onClick={onClose} disabled={loading} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${confirmClass} disabled:opacity-50`}>
            {loading ? <span className="flex items-center gap-2"><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Working...</span> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Snapshot Detail Modal ────────────────────────────────────────────────────
const SnapshotDetailModal = ({ open, onClose, snapshotId }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    if (open && snapshotId) fetchSnapshot();
  }, [open, snapshotId]);

  const fetchSnapshot = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/snapshots/${snapshotId}`);
      if (res.data?.success) setSnapshot(res.data.data);
    } catch { setSnapshot(null); }
    finally { setLoading(false); }
  };

  if (!open) return null;

  // Group data by category
  const byCategory = snapshot?.data
    ? snapshot.data.reduce((acc, item) => {
        const key = item.categoryId;
        if (!acc[key]) acc[key] = { name: item.categoryName, contestants: [] };
        acc[key].contestants.push(item);
        return acc;
      }, {})
    : {};

  const categories = Object.values(byCategory).map(cat => ({
    ...cat,
    contestants: [...cat.contestants].sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0)),
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">📸 Snapshot Details</h2>
            {snapshot && <p className="text-xs text-gray-500 mt-0.5">{snapshot.label}</p>}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>
          ) : !snapshot ? (
            <p className="text-center text-gray-400 py-8">Could not load snapshot data.</p>
          ) : (
            <div className="space-y-4">
              {categories.length === 0 ? (
                <p className="text-center text-gray-400 py-8">No data in this snapshot.</p>
              ) : categories.map((cat, ci) => (
                <div key={ci} className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-4 py-2.5 flex items-center justify-between">
                    <h3 className="text-sm font-bold text-white">{cat.name}</h3>
                    <span className="text-xs text-indigo-200">{cat.contestants.length} contestants</span>
                  </div>
                  <table className="min-w-full text-sm">
                    <thead><tr className="bg-gray-50"><th className="text-left py-2 px-4 text-xs text-gray-400 font-semibold">Rank</th><th className="text-left py-2 px-4 text-xs text-gray-400 font-semibold">Contestant</th><th className="text-right py-2 px-4 text-xs text-gray-400 font-semibold">Votes at Snapshot</th></tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {cat.contestants.map((c, i) => (
                        <tr key={c.contestantId} className={i === 0 ? 'bg-yellow-50' : i === 1 ? 'bg-gray-50' : i === 2 ? 'bg-orange-50' : 'bg-white'}>
                          <td className="py-2.5 px-4 text-sm">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i+1}`}</td>
                          <td className="py-2.5 px-4 font-medium text-gray-900">{c.contestantName}</td>
                          <td className="py-2.5 px-4 text-right font-bold text-indigo-600">{(c.voteCount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const SnapshotsPage = () => {
  const { success, error: showError } = useToast();
  const [contests,         setContests]         = useState([]);
  const [selectedContest,  setSelectedContest]  = useState(null);
  const [snapshots,        setSnapshots]        = useState([]);
  const [loadingContests,  setLoadingContests]  = useState(true);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [takingSnapshot,   setTakingSnapshot]   = useState(false);
  const [snapshotNote,     setSnapshotNote]     = useState('');
  const [pagination,       setPagination]       = useState({ page: 1, total: 0, totalPages: 1 });
  const [confirmRestore,   setConfirmRestore]   = useState(null); // snapshot object
  const [restoring,        setRestoring]        = useState(false);
  const [viewSnapshot,     setViewSnapshot]     = useState(null); // snapshotId

  useEffect(() => { fetchContests(); }, []);
  useEffect(() => { if (selectedContest) fetchSnapshots(selectedContest.id, 1); }, [selectedContest]);

  const fetchContests = async () => {
    try {
      setLoadingContests(true);
      const res = await axiosInstance.get('/snapshots');
      if (res.data?.success) setContests(res.data.data || []);
    } catch (err) {
      showError(handleApiError(err));
    } finally {
      setLoadingContests(false);
    }
  };

  const fetchSnapshots = async (contestId, page = 1) => {
    try {
      setLoadingSnapshots(true);
      const res = await axiosInstance.get(`/snapshots/contest/${contestId}?page=${page}&pageSize=20`);
      if (res.data?.success) {
        setSnapshots(res.data.snapshots || []);
        setPagination(res.data.pagination || { page: 1, total: 0, totalPages: 1 });
      }
    } catch (err) {
      showError(handleApiError(err));
    } finally {
      setLoadingSnapshots(false);
    }
  };

  const handleTakeSnapshot = async () => {
    if (!selectedContest) return;
    try {
      setTakingSnapshot(true);
      const res = await axiosInstance.post(`/snapshots/contest/${selectedContest.id}`, { note: snapshotNote });
      if (res.data?.success) {
        success('Snapshot taken successfully');
        setSnapshotNote('');
        fetchSnapshots(selectedContest.id, 1);
        fetchContests(); // update counts
      }
    } catch (err) {
      showError(handleApiError(err));
    } finally {
      setTakingSnapshot(false);
    }
  };

  const handleRestore = async () => {
    if (!confirmRestore) return;
    try {
      setRestoring(true);
      const res = await axiosInstance.post(`/snapshots/${confirmRestore.id}/restore`);
      if (res.data?.success) {
        success(`✅ Restored! ${res.data.contestantsRestored} contestants updated to snapshot "${confirmRestore.label}"`);
        setConfirmRestore(null);
        fetchSnapshots(selectedContest.id, pagination.page);
      }
    } catch (err) {
      showError(handleApiError(err));
    } finally {
      setRestoring(false);
    }
  };

  const handleDeleteSnapshot = async (snapshotId) => {
    try {
      await axiosInstance.delete(`/snapshots/${snapshotId}`);
      success('Snapshot deleted');
      fetchSnapshots(selectedContest.id, pagination.page);
      fetchContests();
    } catch (err) {
      showError(handleApiError(err));
    }
  };

  const formatDate = (d) => new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <DashboardLayout title="Vote Snapshots" subtitle="Hourly backups of vote counts — restore any contest to a previous state">

      <ConfirmDialog
        open={!!confirmRestore}
        onClose={() => setConfirmRestore(null)}
        onConfirm={handleRestore}
        loading={restoring}
        title="Restore Snapshot"
        confirmLabel="Yes, Restore"
        confirmClass="bg-indigo-600 hover:bg-indigo-700"
        message={confirmRestore
          ? `This will update all contestant vote counts in "${selectedContest?.title}" back to the values recorded at:\n\n"${confirmRestore.label}"\n\nA "Before Restore" snapshot will be saved automatically so you can undo this. Vote and payment records are NOT deleted.`
          : ''}
      />

      <SnapshotDetailModal
        open={!!viewSnapshot}
        onClose={() => setViewSnapshot(null)}
        snapshotId={viewSnapshot}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── LEFT: Contest list ─────────────────────────────────────── */}
        <div className="lg:col-span-1">
          <Card>
            <h3 className="text-sm font-bold text-gray-700 mb-3">Select Contest</h3>
            {loadingContests ? (
              <div className="flex items-center justify-center py-8"><div className="w-5 h-5 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>
            ) : contests.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No contests found</p>
            ) : (
            //   <div className="space-y-2">
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                {contests.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedContest(c)}
                    className={`w-full text-left px-3 py-3 rounded-lg border transition-colors ${selectedContest?.id === c.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-100 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-gray-900 truncate mr-2">{c.title}</span>
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>{c.snapshotCount || 0} snapshot{c.snapshotCount !== 1 ? 's' : ''}</span>
                      {c.latestSnapshot && <span>{formatDate(c.latestSnapshot.takenAt)}</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* ── RIGHT: Snapshots for selected contest ─────────────────── */}
        <div className="lg:col-span-2">
          {!selectedContest ? (
            <Card>
              <div className="text-center py-16">
                <span className="text-5xl">📸</span>
                <p className="mt-3 text-gray-500 text-sm">Select a contest on the left to view its snapshots</p>
              </div>
            </Card>
          ) : (
            <>
              {/* Take snapshot panel */}
              <Card className="mb-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-gray-800 mb-1">Take Manual Snapshot</h3>
                    <p className="text-xs text-gray-400 mb-3">
                      Save the current vote counts for <strong>{selectedContest.title}</strong>. Do this before any bulk operation.
                    </p>
                    <input
                      type="text"
                      value={snapshotNote}
                      onChange={e => setSnapshotNote(e.target.value)}
                      placeholder="Optional note (e.g. 'Before completing bulk payments')"
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 mb-3"
                    />
                    <button
                      onClick={handleTakeSnapshot}
                      disabled={takingSnapshot}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                    >
                      {takingSnapshot
                        ? <><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Taking...</>
                        : <><span>📸</span> Take Snapshot Now</>
                      }
                    </button>
                  </div>
                </div>
              </Card>

              {/* Snapshot list */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-gray-800">
                    Snapshot History
                    <span className="ml-2 text-xs font-normal text-gray-400">({pagination.total} total)</span>
                  </h3>
                  <button onClick={() => fetchSnapshots(selectedContest.id, pagination.page)} className="text-xs text-indigo-500 hover:underline">Refresh</button>
                </div>

                {loadingSnapshots ? (
                  <div className="flex items-center justify-center py-10"><div className="w-6 h-6 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>
                ) : snapshots.length === 0 ? (
                  <div className="text-center py-10">
                    <span className="text-3xl">📭</span>
                    <p className="text-sm text-gray-400 mt-2">No snapshots yet. The first hourly snapshot will be taken at the top of the next hour.</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {snapshots.map(snap => (
                        <div key={snap.id} className={`border rounded-xl px-4 py-3 flex items-center justify-between gap-3 ${snap.label.startsWith('Before Restore') ? 'border-amber-200 bg-amber-50' : snap.isAutomatic ? 'border-gray-100 bg-white' : 'border-indigo-100 bg-indigo-50'}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-0.5">
                              <span className="text-sm font-semibold text-gray-900 truncate">{snap.label}</span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${snap.isAutomatic ? 'bg-gray-100 text-gray-500' : 'bg-indigo-100 text-indigo-700'}`}>
                                {snap.isAutomatic ? 'Auto' : 'Manual'}
                              </span>
                              {snap.label.startsWith('Before Restore') && (
                                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Safety backup</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400">
                              {formatDate(snap.takenAt)}
                              {snap.takenByUser && ` · by ${snap.takenByUser.firstName} ${snap.takenByUser.lastName}`}
                              {snap.note && <span className="text-gray-500"> · {snap.note}</span>}
                            </p>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* View */}
                            <button
                              onClick={() => setViewSnapshot(snap.id)}
                              className="px-2.5 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                            >
                              View
                            </button>

                            {/* Restore */}
                            <button
                              onClick={() => setConfirmRestore(snap)}
                              className="px-2.5 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                            >
                              Restore
                            </button>

                            {/* Delete */}
                            <button
                              onClick={() => handleDeleteSnapshot(snap.id)}
                              className="px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                        <button
                          onClick={() => fetchSnapshots(selectedContest.id, pagination.page - 1)}
                          disabled={pagination.page <= 1}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 transition-colors"
                        >← Prev</button>
                        <span className="text-xs text-gray-400">Page {pagination.page} of {pagination.totalPages}</span>
                        <button
                          onClick={() => fetchSnapshots(selectedContest.id, pagination.page + 1)}
                          disabled={pagination.page >= pagination.totalPages}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 transition-colors"
                        >Next →</button>
                      </div>
                    )}
                  </>
                )}
              </Card>
            </>
          )}
        </div>
      </div>

      {/* Info cards at bottom */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {[
          { icon: '⏰', title: 'Automatic Hourly', desc: 'Vote counts for all active contests are saved every hour automatically.' },
          { icon: '📸', title: 'Manual Snapshots', desc: 'Take a snapshot before any risky bulk operation as an instant safety net.' },
          { icon: '↩️', title: 'Safe Restore', desc: 'Restoring only updates voteCount numbers. No votes or payments are deleted.' },
        ].map(({ icon, title, desc }) => (
          <div key={title} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="text-2xl mb-2">{icon}</div>
            <h4 className="text-sm font-bold text-gray-800 mb-1">{title}</h4>
            <p className="text-xs text-gray-500">{desc}</p>
          </div>
        ))}
      </div>

    </DashboardLayout>
  );
};

export default SnapshotsPage;